import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { TextField } from '@/components/form-fields';
import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ACCOUNT_SUMMARIES_KEY } from '@/lib/accounts';
import { useAuth } from '@/lib/auth';
import {
  getAccount,
  getSubscription,
  listOwnerCandidates,
  saveAccount,
  transferOwnership,
} from '@/lib/settings/account';
import { useForm } from '@/lib/use-form';

/**
 * The workspace itself, the person signed in, and what the plan is.
 *
 * Ownership transfer and the profile controls are the two places in the
 * console where the operator can lock themselves out, so both ask for the
 * thing being changed to be typed back rather than relying on a dialog that
 * everyone learns to dismiss.
 */

const accountSchema = z.object({
  name: z.string().trim().min(1, 'Name the workspace').max(120),
  pictureUrl: z.string().trim().max(500),
});

type AccountValues = z.infer<typeof accountSchema>;

export function AccountSettings({ accountId }: { accountId: string }) {
  const { can, session } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();
  const [confirmName, setConfirmName] = useState('');
  const [newOwner, setNewOwner] = useState('');

  const account = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId),
  });

  const owners = useQuery({
    queryKey: ['owner-candidates', accountId],
    queryFn: () => listOwnerCandidates(accountId),
  });

  const { values, errors, isDirty, set, reset, submit } = useForm<AccountValues>(
    { name: account.data?.name ?? '', pictureUrl: account.data?.pictureUrl ?? '' },
    accountSchema,
  );

  useEffect(() => {
    if (account.data) {
      reset({ name: account.data.name, pictureUrl: account.data.pictureUrl ?? '' });
    }
  }, [account.data, reset]);

  const save = useMutation({
    mutationFn: (parsed: AccountValues) =>
      saveAccount(accountId, { name: parsed.name, pictureUrl: parsed.pictureUrl || null }),
    onSuccess: () => {
      toast.success('Workspace saved.');
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_SUMMARIES_KEY] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const transfer = useMutation({
    mutationFn: () => transferOwnership(accountId, newOwner),
    onSuccess: () => {
      toast.success('Ownership transferred.');
      setConfirmName('');
      setNewOwner('');
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (account.isLoading) return <SettingsSkeleton />;

  const isPrimaryOwner = account.data?.primaryOwnerUserId === session?.user.id;
  const candidates = (owners.data ?? []).filter(
    (member) => member.userId !== account.data?.primaryOwnerUserId,
  );

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate>
        <SectionCard
          title="Workspace"
          description="The name staff see in the console. The trading name customers see lives under Store details."
        >
          <fieldset disabled={!canManage} className="flex flex-col gap-4">
            <TextField
              id="account-name"
              label="Name"
              value={values.name}
              error={errors.name}
              onChange={(value) => set('name', value)}
            />

            <TextField
              id="account-picture"
              label="Logo URL"
              description="Shown beside the workspace name in the sidebar."
              placeholder="https://…"
              value={values.pictureUrl}
              error={errors.pictureUrl}
              onChange={(value) => set('pictureUrl', value)}
            />

            <div className="flex gap-2">
              <Button type="submit" disabled={!isDirty || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              {isDirty && (
                <Button type="button" variant="ghost" onClick={() => reset()}>
                  Discard
                </Button>
              )}
            </div>
          </fieldset>
        </SectionCard>
      </form>

      <SectionCard
        title="Ownership"
        description="The primary owner is the only person who can rename the trading identity, transfer ownership or close the workspace."
      >
        <p className="text-sm">
          Currently{' '}
          <span className="font-medium">
            {owners.data?.find((member) => member.userId === account.data?.primaryOwnerUserId)
              ?.name ?? 'the account creator'}
          </span>
          .
        </p>

        {isPrimaryOwner ? (
          candidates.length ? (
            <div className="flex flex-col gap-3">
              <ListShell>
                {candidates.map((member) => (
                  <ListRow
                    key={member.userId}
                    title={member.name}
                    subtitle={member.email ?? undefined}
                    badges={<Badge variant="secondary">{member.role}</Badge>}
                    actions={
                      <Button
                        type="button"
                        size="sm"
                        variant={newOwner === member.userId ? 'default' : 'ghost'}
                        onClick={() => setNewOwner(member.userId)}
                      >
                        {newOwner === member.userId ? 'Chosen' : 'Choose'}
                      </Button>
                    }
                  />
                ))}
              </ListShell>

              {newOwner && (
                <div className="flex flex-col gap-2 rounded-lg border border-dashed p-4">
                  <p className="text-sm">
                    Transferring makes them the primary owner and demotes you to owner. Type{' '}
                    <span className="font-medium">{account.data?.name}</span> to confirm.
                  </p>
                  <TextField
                    id="transfer-confirm"
                    label="Workspace name"
                    value={confirmName}
                    onChange={setConfirmName}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={confirmName !== account.data?.name || transfer.isPending}
                      onClick={() => transfer.mutate()}
                    >
                      Transfer ownership
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setNewOwner('')}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState>
              Nobody else to transfer to. Invite another owner under Team members first.
            </EmptyState>
          )
        ) : (
          <p className="text-muted-foreground text-sm">
            Only the primary owner can transfer ownership.
          </p>
        )}
      </SectionCard>

      <BillingSummary accountId={accountId} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Profile                                                                     */
/* -------------------------------------------------------------------------- */

const profileSchema = z.object({
  name: z.string().trim().max(120),
  email: z.string().trim().email('Enter a valid email'),
});

const passwordSchema = z
  .object({
    password: z.string().min(8, 'Use at least 8 characters'),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: 'Those do not match',
    path: ['confirm'],
  });

export function ProfileSettings() {
  const { session } = useAuth();
  const user = session?.user;

  const { values, errors, isDirty, set, submit } = useForm(
    {
      name: (user?.user_metadata?.name as string | undefined) ?? '',
      email: user?.email ?? '',
    },
    profileSchema,
  );

  const password = useForm({ password: '', confirm: '' }, passwordSchema);

  const saveProfile = useMutation({
    mutationFn: async (parsed: z.infer<typeof profileSchema>) => {
      const { updateProfile } = await import('@/lib/settings/account');

      await updateProfile(parsed);
    },
    onSuccess: (_, parsed) =>
      toast.success(
        parsed.email === user?.email
          ? 'Profile saved.'
          : 'Profile saved. Confirm the new address from the email we just sent.',
      ),
    onError: (error: Error) => toast.error(error.message),
  });

  const savePassword = useMutation({
    mutationFn: async (parsed: z.infer<typeof passwordSchema>) => {
      const { updatePassword } = await import('@/lib/settings/account');

      await updatePassword(parsed.password);
    },
    onSuccess: () => {
      password.reset({ password: '', confirm: '' });
      toast.success('Password changed.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <form onSubmit={submit((parsed) => saveProfile.mutate(parsed))} noValidate>
        <SectionCard
          title="Your details"
          description="Your name appears on the orders and notes you touch."
        >
          <TextField
            id="profile-name"
            label="Name"
            value={values.name}
            error={errors.name}
            onChange={(value) => set('name', value)}
          />

          <TextField
            id="profile-email"
            label="Email"
            type="email"
            description="Changing this sends a confirmation link to the new address; the old one keeps working until you click it."
            value={values.email}
            error={errors.email}
            onChange={(value) => set('email', value)}
          />

          <Button type="submit" disabled={!isDirty || saveProfile.isPending}>
            {saveProfile.isPending ? 'Saving…' : 'Save'}
          </Button>
        </SectionCard>
      </form>

      <form onSubmit={password.submit((parsed) => savePassword.mutate(parsed))} noValidate>
        <SectionCard
          title="Password"
          description="Signs you out of nothing else; other devices stay signed in."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="profile-password"
              label="New password"
              type="password"
              value={password.values.password}
              error={password.errors.password}
              onChange={(value) => password.set('password', value)}
            />
            <TextField
              id="profile-confirm"
              label="Confirm"
              type="password"
              value={password.values.confirm}
              error={password.errors.confirm}
              onChange={(value) => password.set('confirm', value)}
            />
          </div>

          <Button type="submit" disabled={!password.values.password || savePassword.isPending}>
            {savePassword.isPending ? 'Saving…' : 'Change password'}
          </Button>
        </SectionCard>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Billing                                                                     */
/* -------------------------------------------------------------------------- */

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  paused: 'Paused',
};

export function BillingSettings({ accountId }: { accountId: string }) {
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <BillingSummary accountId={accountId} />
    </div>
  );
}

function BillingSummary({ accountId }: { accountId: string }) {
  const subscription = useQuery({
    queryKey: ['subscription', accountId],
    queryFn: () => getSubscription(accountId),
  });

  if (subscription.isLoading) return <SettingsSkeleton />;

  const record = subscription.data;

  return (
    <SectionCard
      title="Plan"
      description="Read-only here. Changing a plan or a card happens in the billing portal."
    >
      {record ? (
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-lg border p-3">
            <dt className="text-muted-foreground text-xs">Status</dt>
            <dd className="text-base font-semibold">
              {STATUS_LABELS[record.status] ?? record.status}
            </dd>
          </div>
          <div className="rounded-lg border p-3">
            <dt className="text-muted-foreground text-xs">Renews</dt>
            <dd className="text-base font-semibold">
              {record.periodEndsAt
                ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                    new Date(record.periodEndsAt),
                  )
                : '—'}
            </dd>
          </div>
          <div className="rounded-lg border p-3">
            <dt className="text-muted-foreground text-xs">Billed through</dt>
            <dd className="text-base font-semibold capitalize">{record.provider ?? '—'}</dd>
          </div>
        </dl>
      ) : (
        <EmptyState>
          No subscription on this workspace. Everything is available; nothing is being charged.
        </EmptyState>
      )}
    </SectionCard>
  );
}
